import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import {
  StringCustomFieldConfig,
  SharedModule,
  FormInputComponent,
  NotificationService,
  DynamicFormInputComponent
} from '@vendure/admin-ui/core';
import { CardCarouselBannerData, TextAlignment, defaultCardCarouselBannerData, BasicCardData } from '../ui-types';
import { safeJSONParse } from '../utils';
import { notNullOrUndefined } from '@vendure/common/lib/shared-utils';
import { Observable } from 'rxjs';
import { DEFAULT_ACTIVE_LANGUAGE } from '../constants';

@Component({
  template: `
    <vdr-card>
      <div class="clr-row mb-2">
        <vdr-form-field class="clr-col-6" label="Text Alignment">
          <select [formControl]="textAlignmentControl">
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </vdr-form-field>
        <vdr-form-field class="clr-col-6" [label]="'Text Color'">
          <input type="color" [formControl]="textColorControl">
        </vdr-form-field>
      </div>
      <div class="clr-row mb-2">
        <vdr-form-field class="clr-col-6" label="Title Font Size (px)" hint="Positive integer">
          <input
            type="number"
            min="1"
            step="1"
            [formControl]="titleFontSizeControl"
          />
        </vdr-form-field>
        <vdr-form-field class="clr-col-6" label="Body Text Font Size (px)" hint="Positive integer">
          <input
            type="number"
            min="1"
            step="1"
            [formControl]="textFontSizeControl"
          />
        </vdr-form-field>
      </div>
      <div class="clr-row mb-2">
        <vdr-form-field [label]="'Card Shape'" class="clr-col-6">
          <select [formControl]="basicCardShapeControl">
            <option value="rectangle-16-9">Rectangle(16:9)</option>
            <option value="rectangle-9-16">Rectangle(9:16)</option>
            <option value="rectangle-7-5">Rectangle(7:5)</option>
            <option value="rectangle-5-7">Rectangle(5:7)</option>
            <option value="square">Square</option>
            <option value="circle">Circle</option>
          </select>
        </vdr-form-field>
        <vdr-form-field [label]="'Cards Per Row'" class="clr-col-6">
          <select [formControl]="slideNumberControl">
            <option *ngFor="let num of [1, 2, 3, 4]" [value]="num">{{ num }}</option>
          </select>
        </vdr-form-field>
      </div>
      <div class="clr-row mb-2">
        <vdr-form-field [label]="'Media Display Rounded Corners'" class="clr-col-6">
          <select [formControl]="mediaRoundedCornersControl">
            <option value="none">None</option>
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
            <option value="full">Full</option>
          </select>
        </vdr-form-field>
        <vdr-form-field [label]="'Mobile Display Style'" class="clr-col-6">
          <select [formControl]="mobileStyleControl">
            <option value="slide">Slide</option>
            <option value="tiles">Tiles</option>
          </select>
        </vdr-form-field>
      </div>

      <div class="clr-row mb-2">
        <vdr-form-field class="clr-col-12" [label]="'Background Color'">
          <input type="color" [formControl]="backgroundColorControl">
        </vdr-form-field>
      </div>
      <vdr-form-field [label]="'Cards'">
        <vdr-dynamic-form-input #basicCardsInput
          [control]="basicCardsControl"
          [def]="basicCardInputDef"
        >
        </vdr-dynamic-form-input>
      </vdr-form-field>
    </vdr-card>
  `,
  standalone: true,
  imports: [SharedModule],
})
export class CardCarouselBannerFormComponent
  implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit
{
  @ViewChild('basicCardsInput') basicCardsInput: DynamicFormInputComponent;

  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  basicCardShapeControl = new FormControl();
  slideNumberControl = new FormControl();
  mobileStyleControl = new FormControl();
  titleFontSizeControl = new FormControl();
  subtitleFontSizeControl = new FormControl();
  textFontSizeControl = new FormControl();
  titleAlignmentControl = new FormControl();
  textAlignmentControl = new FormControl();
  backgroundColorControl = new FormControl();
  textColorControl = new FormControl();
  mediaRoundedCornersControl = new FormControl();
  basicCardsControl = new UntypedFormControl();
  basicCardInputDef: any = {
    list: true,
    type: 'string',
    ui: {
      component: 'basic-card-form',
      args: {},
    },
  };

  cardCarouselBannerData: CardCarouselBannerData;
  private activeLanguageCode = DEFAULT_ACTIVE_LANGUAGE;
  private languageChanges$?: Observable<unknown>;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    const args = (this.config as any)?.ui?.args ?? {};
    if (typeof args.activeLanguageCode === 'string' && args.activeLanguageCode) {
      this.activeLanguageCode = args.activeLanguageCode;
    }
    if (args?.languageChanges$ && typeof args.languageChanges$.subscribe === 'function') {
      this.languageChanges$ = args.languageChanges$ as Observable<unknown>;
    }
    this.basicCardInputDef = {
      ...this.basicCardInputDef,
      ui: {
        ...this.basicCardInputDef.ui,
        args: {
          activeLanguageCode: this.activeLanguageCode,
          languageChanges$: this.languageChanges$,
        },
      },
    };
    this.cardCarouselBannerData = safeJSONParse<CardCarouselBannerData>(
      this.formControl.value,
      this.notificationService,
      defaultCardCarouselBannerData,
    )!;
    this.initializeFormControls();
  }

  ngAfterViewInit() {
    this.basicCardShapeControl.valueChanges.subscribe(this.onBasicCardShapeChange);
    this.slideNumberControl.valueChanges.subscribe(this.onSlideNumberChange);
    this.mobileStyleControl.valueChanges.subscribe(this.onMobileStyleChange);
    this.titleFontSizeControl.valueChanges.subscribe(this.onTitleFontSizeChange);
    this.subtitleFontSizeControl.valueChanges.subscribe(this.onSubtitleFontSizeChange);
    this.textFontSizeControl.valueChanges.subscribe(this.onTextFontSizeChange);
    this.titleAlignmentControl.valueChanges.subscribe(this.onTitleAlignmentChange);
    this.textAlignmentControl.valueChanges.subscribe(this.onTextAlignmentChange);
    this.backgroundColorControl.valueChanges.subscribe(this.onBackgroundColorChange);
    this.textColorControl.valueChanges.subscribe(this.onTextColorChange);
    this.mediaRoundedCornersControl.valueChanges.subscribe(this.onMediaRoundedCornersChange);
    if (this.basicCardsInput) {
      this.basicCardsInput.registerOnChange(this.onBasicCardsChange);
      if(this.cardCarouselBannerData?.basicCards) {
        const basicCards = this.cardCarouselBannerData?.basicCards?.map((value) => JSON.stringify(value)) || [];
        this.basicCardsInput.writeValue(basicCards);
      }
    }
    this.changeDetector.markForCheck();
  }

  private initializeFormControls() {
    this.basicCardShapeControl.setValue(this.cardCarouselBannerData?.basicCardShape ?? null);
    this.slideNumberControl.setValue(this.cardCarouselBannerData?.slideNumber ?? 4);
    this.mobileStyleControl.setValue(this.cardCarouselBannerData?.mobileStyle ?? 'tiles');
    this.titleFontSizeControl.setValue(this.cardCarouselBannerData?.titleFontSize ?? 32);
    this.subtitleFontSizeControl.setValue(this.cardCarouselBannerData?.subtitleFontSize ?? 24);
    this.textFontSizeControl.setValue(this.cardCarouselBannerData?.textFontSize ?? 20);
    this.titleAlignmentControl.setValue(this.cardCarouselBannerData?.titleAlignment ?? TextAlignment.Center);
    this.textAlignmentControl.setValue(this.cardCarouselBannerData?.textAlignment ?? TextAlignment.Center);
    this.backgroundColorControl.setValue(this.cardCarouselBannerData?.backgroundColor);
    this.textColorControl.setValue(this.cardCarouselBannerData?.textColor);
    this.mediaRoundedCornersControl.setValue(this.cardCarouselBannerData?.mediaRoundedCorners || 'none');
  }

  private onBasicCardsChange = (values: string[]) => {
    if (this.cardCarouselBannerData && JSON.stringify(this.cardCarouselBannerData.basicCards) !== JSON.stringify(values)) {
      const defaultBasicCardData: BasicCardData = {
        assetId: null,
        title: { format: 'plain', i18n: { [this.activeLanguageCode]: '' } },
        text: { format: 'plain', i18n: { [this.activeLanguageCode]: '' } },
      };
      this.cardCarouselBannerData.basicCards = values?.map((value) => safeJSONParse<BasicCardData>(
        value,
        this.notificationService,
        defaultBasicCardData
      )).filter(notNullOrUndefined) ?? [];
      this.updateJsonString();
    }
  };

  private onBasicCardShapeChange = (value: string | null) => {
    if (this.cardCarouselBannerData?.basicCardShape !== value) {
      this.cardCarouselBannerData!.basicCardShape = value ?? undefined;
      this.updateJsonString();
    }
  };

  private onSlideNumberChange = (value: number) => {
    if (this.cardCarouselBannerData?.slideNumber !== value) {
      this.cardCarouselBannerData!.slideNumber = value;
      this.updateJsonString();
    }
  };

  private onMobileStyleChange = (value: string) => {
    if (this.cardCarouselBannerData?.mobileStyle !== value) {
      this.cardCarouselBannerData!.mobileStyle = value;
      this.updateJsonString();
    }
  };

  private onTitleFontSizeChange = (value: number) => {
    if (this.cardCarouselBannerData?.titleFontSize !== value) {
      this.cardCarouselBannerData!.titleFontSize = value;
      this.updateJsonString();
    }
  };

  private onSubtitleFontSizeChange = (value: number) => {
    if (this.cardCarouselBannerData?.subtitleFontSize !== value) {
      this.cardCarouselBannerData!.subtitleFontSize = value;
      this.updateJsonString();
    }
  };

  private onTextFontSizeChange = (value: number) => {
    if (this.cardCarouselBannerData?.textFontSize !== value) {
      this.cardCarouselBannerData!.textFontSize = value;
      this.updateJsonString();
    }
  };

  private onTitleAlignmentChange = (value: TextAlignment) => {
    if (this.cardCarouselBannerData?.titleAlignment !== value) {
      this.cardCarouselBannerData!.titleAlignment = value;
      this.updateJsonString();
    }
  };

  private onTextAlignmentChange = (value: TextAlignment) => {
    if (this.cardCarouselBannerData?.textAlignment !== value) {
      this.cardCarouselBannerData!.textAlignment = value;
      this.updateJsonString();
    }
  };

  private onBackgroundColorChange = (value: string) => {
    if (this.cardCarouselBannerData?.backgroundColor !== value) {
      this.cardCarouselBannerData!.backgroundColor = value;
      this.updateJsonString();
    }
  };

  private onTextColorChange = (value: string) => {
    if (this.cardCarouselBannerData?.textColor !== value) {
      this.cardCarouselBannerData!.textColor = value;
      this.updateJsonString();
    }
  };

  private onMediaRoundedCornersChange = (value: string) => {
    if (this.cardCarouselBannerData?.mediaRoundedCorners !== value) {
      this.cardCarouselBannerData!.mediaRoundedCorners = value;
      this.updateJsonString();
    }
  };

  private updateJsonString() {
    const jsonString = JSON.stringify(this.cardCarouselBannerData);
    this.formControl.setValue(jsonString);
    this.formControl.markAsDirty();
  }
}
